import { useState, useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function AttendanceCalendar({
  attendanceData,
  absenceData,
  userRole,
}) {
  console.log(attendanceData);
  console.log(absenceData);

  const [currentDate, setCurrentDate] = useState(new Date());

  const monthNames = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];

  const dayNames = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

  const formatTimeFromDate = (dateString) => {
    if (!dateString) return null;

    try {
      // Create a date object from the string
      const date = new Date(dateString);

      // Extract hours and minutes
      const hours = date.getUTCHours().toString().padStart(2, "0");
      const minutes = date.getUTCMinutes().toString().padStart(2, "0");

      return `${hours}:${minutes}`;
    } catch (error) {
      console.error("Error formatting time:", error);
      return null;
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month - create them consistently in local time
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getAttendanceForDate = (date) => {
    if (!date || !attendanceData) return null;

    // Format the calendar date as YYYY-MM-DD
    const dateStr = formatDateLocal(date);

    return attendanceData.find((att) => {
      if (!att.date_presence) return false;

      // Extract just the date part from the UTC datetime
      const dbDateStr = att.date_presence.split("T")[0];

      return dbDateStr === dateStr;
    });
  };

  const getAbsencesForDate = (date) => {
    if (!date || !absenceData) return [];

    // Format the calendar date as YYYY-MM-DD
    const dateStr = formatDateLocal(date);

    return absenceData.filter((absence) => {
      if (absence.statut !== "validee") return false;

      const startDateStr = absence.date_debut.split("T")[0];
      const endDateStr = absence.date_fin.split("T")[0];

      return dateStr >= startDateStr && dateStr <= endDateStr;
    });
  };

  const getDayStatus = (date) => {
    if (!date) return null;

    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    const isPast = date < today;
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;

    const attendance = getAttendanceForDate(date);
    const absences = getAbsencesForDate(date);

    // Priority 1: Check for absences
    if (absences.length > 0) {
      return {
        type: "absence",
        label:
          absences.length === 1 ? "Absence" : `${absences.length} Absences`,
        className: "bg-red-100 text-red-800 border-red-200",
        count: absences.length,
      };
    }

    // Priority 2: Check attendance
    if (attendance) {
      console.log(attendance);

      if (attendance.heure_arrivee && attendance.heure_depart) {
        return {
          type: "present",
          label: "Présent",
          className: "bg-green-100 text-green-800 border-green-200",
        };
      } else if (attendance.heure_arrivee) {
        return {
          type: "partial",
          label: "En cours",
          className: "bg-blue-100 text-blue-800 border-blue-200",
        };
      }
    }

    // Priority 3: Past working days without attendance
    if (isPast && !isWeekend) {
      return {
        type: "missing",
        label: "Absent",
        className: "bg-gray-100 text-gray-600 border-gray-200",
      };
    }

    // Priority 4: Weekends
    if (isWeekend) {
      return {
        type: "weekend",
        label: "Week-end",
        className: "bg-gray-50 text-gray-400",
      };
    }

    // Priority 5: Future days
    return {
      type: "future",
      label: "À venir",
      className: "bg-white text-gray-900",
    };
  };

  const days = getDaysInMonth(currentDate);

  const previousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const monthStats = useMemo(() => {
    if (!attendanceData || !absenceData)
      return { present: 0, absent: 0, partial: 0 };

    const monthStart = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );
    const monthEnd = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0
    );

    let present = 0,
      absent = 0,
      partial = 0;

    const monthDays = getDaysInMonth(currentDate).filter((day) => day !== null);

    monthDays.forEach((day) => {
      if (day <= new Date() && day.getDay() !== 0 && day.getDay() !== 6) {
        const status = getDayStatus(day);
        if (status?.type === "present") present++;
        else if (status?.type === "partial") partial++;
        else if (status?.type === "missing" || status?.type === "absence")
          absent++;
      }
    });

    return { present, absent, partial };
  }, [currentDate, attendanceData, absenceData]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Calendrier de présence
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToToday}>
              Aujourd'hui
            </Button>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={previousMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[140px] text-center font-medium">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </span>
              <Button variant="outline" size="sm" onClick={nextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Month Statistics */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-700">
              {monthStats.present}
            </p>
            <p className="text-sm text-green-600">Présent</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-700">
              {monthStats.partial}
            </p>
            <p className="text-sm text-blue-600">Partiel</p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-2xl font-bold text-red-700">
              {monthStats.absent}
            </p>
            <p className="text-sm text-red-600">Absent</p>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="space-y-4">
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1">
            {dayNames.map((day) => (
              <div
                key={day}
                className="p-2 text-center text-sm font-medium text-gray-500"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, index) => {
              if (!day) {
                return <div key={index} className="h-24"></div>;
              }

              const status = getDayStatus(day);
              const isToday = day.toDateString() === new Date().toDateString();
              const absences = getAbsencesForDate(day);
              const attendance = getAttendanceForDate(day);

              return (
                <div
                  key={index}
                  className={`h-24 p-2 border rounded-lg transition-all hover:shadow-sm ${
                    status?.className || "bg-white"
                  } ${isToday ? "ring-2 ring-blue-500" : ""}`}
                >
                  <div className="flex flex-col h-full">
                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium">
                        {day.getDate()}
                      </span>
                      {isToday && (
                        <Badge variant="default" className="text-xs px-1 py-0">
                          Aujourd'hui
                        </Badge>
                      )}
                    </div>

                    <div className="flex-1 flex flex-col justify-center">
                      {/* Show absences first */}
                      {absences.length > 0 && (
                        <div className="space-y-1 mb-2">
                          {absences.slice(0, 2).map((absence, idx) => (
                            <div
                              key={`${absence.id}-${idx}`}
                              className="text-xs p-1 rounded bg-red-100 text-red-800 truncate"
                              title={`${absence.employe?.prenom || ""} ${
                                absence.employe?.nom || ""
                              } - ${absence.type_absence?.nom || "Absence"}`}
                            >
                              {userRole !== "employe"
                                ? `${absence.employe?.prenom || ""} ${
                                    absence.employe?.nom?.charAt(0) || ""
                                  }.`
                                : absence.type_absence?.nom || "Absence"}
                            </div>
                          ))}
                          {absences.length > 2 && (
                            <div className="text-xs text-red-600 text-center">
                              +{absences.length - 2} autre(s)
                            </div>
                          )}
                        </div>
                      )}

                      {/* Show attendance status if no absences */}
                      {absences.length === 0 && status && (
                        <div className="text-xs text-center">
                          <div className="font-medium">{status.label}</div>
                          {status.count && (
                            <div className="text-xs opacity-75">
                              {status.count} personne
                              {status.count > 1 ? "s" : ""}
                            </div>
                          )}
                          {attendance && attendance.heure_arrivee && (
                            <div className="text-xs opacity-75 mt-1">
                              {formatTimeFromDate(attendance.heure_arrivee)}
                              {attendance.heure_depart &&
                                ` - ${formatTimeFromDate(
                                  attendance.heure_depart
                                )}`}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-6 pt-4 border-t">
          <h4 className="text-sm font-medium mb-3">Légende</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-100 border border-green-200 rounded"></div>
              <span className="text-xs">Présent</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-100 border border-blue-200 rounded"></div>
              <span className="text-xs">En cours</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-100 border border-red-200 rounded"></div>
              <span className="text-xs">Absence</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-100 border border-gray-200 rounded"></div>
              <span className="text-xs">Non pointé</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
